/* eslint-disable unicorn/filename-case -- TODO rename later */
import { istariLink } from "@istari/istari-authflow";
import { useEffect, useState } from "react";

import FloatingCommandMenu from "~/components/FloatingCommandMenu";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { toast } from "~/components/ui/sonner";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { ConnectFormData, SchemaProps } from "~/schemas/json-schema-utils";
import { isValidUrl } from "~/utils";
import { getAppLabel } from "~/utils/appIntegrationLabels";

function ConnectInputField({
  propertyInfo,
  value = "",
  setValue,
  className,
}: {
  propertyInfo: SchemaProps;
  value?: string | null;
  setValue: (s: string) => void;
  className?: string;
}) {
  if (propertyInfo.type === "string" && propertyInfo.enum) {
    const allowAddOption = propertyInfo.enum.includes("manual_entry");
    return (
      <FloatingCommandMenu
        options={propertyInfo.enum.filter((e) => e !== "manual_entry")}
        heading={"Resource Type"}
        value={value}
        onSelect={setValue}
        allowAddOption={allowAddOption}
      />
    );
  }
  if (propertyInfo.type === "string") {
    return (
      <Input
        className={cn(className, "focus:text-black")}
        value={value ?? ""}
        onChange={(e) => setValue(e.target.value)}
      />
    );
  }
}

function ConnectFileForm({
  handleValidForm,
  formData,
  setFormData,
  schema,
}: {
  handleValidForm: (s: boolean) => void;
  formData: ConnectFormData;
  setFormData: React.Dispatch<React.SetStateAction<ConnectFormData>>;
  schema: SchemaProps | undefined;
}) {
  const [url, setUrl] = useState<string>();
  const [urlAppHost, setUrlAppHost] = useState<string>();
  const appHost = formData.app_host;

  const formProperties = schema?.properties;
  const requiredProperties = schema?.required;

  useEffect(() => {
    if (!requiredProperties) {
      return;
    }
    const validEntries = Object.fromEntries(Object.entries(formData).filter(([, v]) => Boolean(v)));
    const hasRequired = requiredProperties.every((k) => k in validEntries);

    const validUrl = url ? isValidUrl(url) : true;
    handleValidForm(hasRequired && validUrl);
  }, [formData, handleValidForm, requiredProperties, url]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!url || !isValidUrl(url)) {
        return;
      }
      try {
        const metaData = istariLink.generateLinkMetaData({ url });
        setUrlAppHost(metaData.app_host);
        setFormData((prev) => {
          if (metaData.app_host === prev.app_host) {
            return metaData;
          } else {
            toast.error(`Invalid URL for ${getAppLabel(prev.app_host).name}`);
            return prev;
          }
        });
      } catch (e) {
        setUrlAppHost(undefined);
        console.error(e);
        toast.error("No 3rd Party Integrations found for URL");
      }
    }, 800);
    return () => clearTimeout(timeout);
  }, [url, setFormData]);

  const handleFormUpdate = (k: string, v: string) => {
    setFormData((prev) => {
      if (k === "url") {
        setUrl(v ? v : undefined);
        return { url: v, app_host: prev.app_host };
      } else {
        return { ...prev, [k]: v } as ConnectFormData;
      }
    });
  };

  const getUrlErrorTip = (prop: string) => {
    if (prop === "url") {
      const currentUrl = formData["url"] as string;
      if (currentUrl && !isValidUrl(currentUrl)) return "Invalid URL";
      if (currentUrl && urlAppHost !== appHost) return `Invalid URL for ${getAppLabel(appHost).name}`;
    }
  };

  return (
    <div>
      {formProperties &&
        Object.entries(formProperties)
          .filter(([k]) => k !== "app_host")
          .map(([property, propertyInfo]) => {
            let className = "";
            let toolTip = propertyInfo.description;
            const urlErrTip = getUrlErrorTip(property);
            if (urlErrTip) {
              className = "border-red-400 border text-red-400";
              toolTip = urlErrTip;
            }
            const required = requiredProperties?.includes(property);
            return (
              <div key={property} className="pt-2 pb-2">
                <Tooltip>
                  <TooltipTrigger>
                    <Label className={"pb-2"}>
                      {propertyInfo.title}
                      {required && <span className={"text-red-400"}>*</span>}
                    </Label>
                  </TooltipTrigger>
                  <TooltipContent>
                    {requiredProperties?.includes(property) && "Required: "}
                    {toolTip}
                  </TooltipContent>
                </Tooltip>
                <ConnectInputField
                  propertyInfo={propertyInfo}
                  value={formData[property as keyof typeof formData] as string | null | undefined}
                  setValue={(s: string) => handleFormUpdate(property, s)}
                  className={className}
                />
              </div>
            );
          })}
    </div>
  );
}

export default ConnectFileForm;
